import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';

const CAPTURE_METHODS = ['QR Code', 'Tablet', 'Online', 'Business Card'];

const CLUBS = [
  { id: 'WA', name: 'West Allis' },
  { id: 'MF', name: 'Menomonee Falls' },
  { id: 'GB', name: 'Green Bay' },
  { id: 'DEN', name: 'Denton' },
  { id: 'LEW', name: 'Lewisville' },
  { id: 'MCK', name: 'McKinney' },
];

// Same TX/WI split already used elsewhere (e.g. inbound-lead-post's market
// field) — reused here rather than inventing a new grouping.
const CLUB_STATE = { MCK: 'TX', LEW: 'TX', DEN: 'TX', WA: 'WI', MF: 'WI', GB: 'WI' };

const QUICK_ADD_TEMPLATES = [
  { icon: '👤', label: 'First Name', field_type: 'text', field_key: 'first_name', required: true, field_options: [] },
  { icon: '👤', label: 'Last Name', field_type: 'text', field_key: 'last_name', required: true, field_options: [] },
  { icon: '📧', label: 'Email Address', field_type: 'email', field_key: 'email', required: true, field_options: [] },
  { icon: '📱', label: 'Phone Number', field_type: 'phone', field_key: 'phone', required: false, field_options: [] },
  { icon: '🎯', label: 'What would you like to claim?', field_type: 'multiselect', field_key: 'interest', required: true,
    field_options: ['Claim my Free Day Pass', 'Register for a Free Learn to Play class', 'Enter to win a 15-Day Trial', "I'm ready — I want a membership", 'Book a private court session'] },
  { icon: '🏓', label: 'Skill Level', field_type: 'select', field_key: 'skill_level', required: false,
    field_options: ['Never Played', 'Beginner', 'Intermediate', 'Advanced'] },
  { icon: '🎾', label: 'Player Description', field_type: 'select', field_key: 'player_description', required: false,
    field_options: ['Brand new to pickleball', "I've played a few times", 'I play regularly', 'I play competitively'] },
  { icon: '📣', label: 'How did you hear about us?', field_type: 'select', field_key: 'referral_source', required: false,
    field_options: ['Social Media', 'Friend or Family', 'Signage at Event', 'Email', 'Other'] },
  { icon: '📅', label: 'Preferred Visit Date', field_type: 'date', field_key: 'preferred_visit_date', required: false, field_options: [] },
  { icon: '📅', label: 'Preferred Session Date', field_type: 'date', field_key: 'preferred_session_date', required: false, field_options: [] },
  { icon: '👥', label: 'Age Group', field_type: 'select', field_key: 'age_group', required: false,
    field_options: ['Youth (under 18)', 'Adult (18-54)', 'Senior (55+)'] },
  { icon: '💬', label: 'Additional Comments', field_type: 'textarea', field_key: 'comments', required: false, field_options: [] },
];

const FIELD_TYPE_OPTIONS = [
  { value: 'text', label: 'Short Text' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'select', label: 'Single Choice' },
  { value: 'multiselect', label: 'Multiple Choice (select all that apply)' },
  { value: 'textarea', label: 'Long Text' },
  { value: 'date', label: 'Date' },
  { value: 'number', label: 'Number' },
  // Display-only: shows the label as a heading plus a clickable link (e.g. a
  // donation page). No input, so nothing is submitted for it.
  // field_options = [url, link text].
  { value: 'link', label: 'Text + Link (no answer)' },
  // Display-only paragraph(s) with links anywhere in the text, written as
  // [link text](https://...). field_options = [body]. No input/answer.
  { value: 'text_block', label: 'Text Section with links (no answer)' },
];

// Form disclaimer looks — keep in sync with DISCLAIMER_STYLES (AzureApp
// index.js) and .form-disclaimer.<style> on playpicklr.com.
const DISCLAIMER_STYLE_OPTIONS = {
  blue_italic: { label: 'Blue italic', css: { color: '#0075E7', fontStyle: 'italic' } },
  red_bold: { label: 'Red bold', css: { color: '#d32f2f', fontWeight: 700 } },
  plain: { label: 'Plain', css: { color: '#333' } },
};

// Display-only field types — nothing to answer, so never "Required".
const DISPLAY_FIELD_TYPES = ['link', 'text_block'];

// Splits Text Section body into plain-text and link parts. Same rule the
// playpicklr.com form uses: [text](http(s)://url), anything else is literal.
const INLINE_LINK_RE = /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)/g;
function parseInlineLinks(body) {
  const parts = [];
  let last = 0;
  for (const m of String(body || '').matchAll(INLINE_LINK_RE)) {
    if (m.index > last) parts.push({ text: body.slice(last, m.index) });
    parts.push({ text: m[1], href: m[2] });
    last = m.index + m[0].length;
  }
  if (last < String(body || '').length) parts.push({ text: String(body).slice(last) });
  return parts;
}

// Live preview of a choice question as the guest sees it — click an option to
// see its follow-up text box open, same behavior as the playpicklr.com form.
function ChoicePreview({ field }) {
  const [picked, setPicked] = useState([]);
  const opts = (field.field_options || [])
    .map(o => (typeof o === 'string' ? { label: o } : o || {}))
    .filter(o => (o.label || '').trim());
  if (opts.length === 0) return null;
  const multi = field.field_type === 'multiselect';
  const toggle = i => setPicked(p => multi ? (p.includes(i) ? p.filter(x => x !== i) : [...p, i]) : [i]);
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 10, color: '#555', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
        Preview — click an option
      </div>
      <div style={{ background: '#f2f2f2', color: '#111', borderRadius: 10, padding: '12px 14px', fontSize: 14 }}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>{field.field_label || 'Question'}{field.required ? <span style={{ color: '#929699' }}> *</span> : null}</div>
        {opts.map((o, i) => (
          <div key={i}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 4px', cursor: 'pointer' }}>
              <input type={multi ? 'checkbox' : 'radio'} checked={picked.includes(i)} onChange={() => toggle(i)} />
              <span>{o.label.split('|')[0]}</span>
            </label>
            {o.write_in && picked.includes(i) && (
              <input disabled style={{ margin: '2px 0 8px 30px', width: 'calc(100% - 30px)', padding: '8px 10px', borderRadius: 8, border: '1px solid #ccc', background: '#fff' }}
                placeholder={(o.placeholder || '') + (o.write_in === 'required' ? ' *' : ' (optional)')} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Builder editor for a Text Section: textarea + "Insert link" + live preview.
function TextSectionEditor({ value, onChange, labelStyle }) {
  const taRef = useRef(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkText, setLinkText] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const sel = useRef({ start: 0, end: 0 });

  function openLink() {
    const ta = taRef.current;
    const start = ta ? ta.selectionStart : value.length;
    const end = ta ? ta.selectionEnd : value.length;
    sel.current = { start, end };
    setLinkText(value.slice(start, end));
    setLinkUrl('');
    setLinkOpen(true);
  }
  const urlOk = /^https?:\/\/\S+$/i.test(linkUrl.trim());
  function insertLink() {
    if (!urlOk) return;
    const text = (linkText.trim() || linkUrl.trim()).replace(/[[\]]/g, '');
    const md = `[${text}](${linkUrl.trim()})`;
    const { start, end } = sel.current;
    onChange(value.slice(0, start) + md + value.slice(end));
    setLinkOpen(false);
  }

  const btn = { background: 'transparent', color: '#aaa', border: '1px solid var(--border)', borderRadius: '8px', padding: '6px 12px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' };
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <label style={labelStyle}>Section Text</label>
        <button type="button" style={btn} onClick={openLink}>🔗 Insert link</button>
      </div>
      <textarea ref={taRef} className="field-input" rows={5} style={{ fontSize: 13, padding: '8px 10px', resize: 'vertical', width: '100%' }}
        placeholder="Type your text. Highlight a word and click Insert link to turn it into a link."
        value={value} onChange={e => onChange(e.target.value)} />
      {linkOpen && (
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 10, marginTop: 8, display: 'grid', gap: 8 }}>
          <input className="field-input" style={{ fontSize: 13, padding: '6px 10px' }} placeholder="Words to show (e.g. donate here)"
            value={linkText} onChange={e => setLinkText(e.target.value)} />
          <input className="field-input" style={{ fontSize: 13, padding: '6px 10px' }} placeholder="https://..." autoFocus
            value={linkUrl} onChange={e => setLinkUrl(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); insertLink(); } }} />
          {linkUrl && !urlOk && <div style={{ color: 'var(--red, #e5484d)', fontSize: 12 }}>Link must start with https://</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" style={btn} onClick={() => setLinkOpen(false)}>Cancel</button>
            <button type="button" style={{ ...btn, color: urlOk ? 'var(--yellow)' : '#555' }} disabled={!urlOk} onClick={insertLink}>Add link</button>
          </div>
        </div>
      )}
      {value.trim() && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 10, color: '#555', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Preview</div>
          <div style={{ background: '#f2f2f2', color: '#333', borderRadius: 8, padding: '10px 12px', fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-line' }}>
            {parseInlineLinks(value).map((p, i) => p.href
              ? <a key={i} href={p.href} target="_blank" rel="noopener noreferrer" style={{ color: '#0075E7', textDecoration: 'underline' }}>{p.text}</a>
              : <span key={i}>{p.text}</span>)}
          </div>
        </div>
      )}
    </div>
  );
}

const DEFAULT_FIELDS = [
  { field_key: 'first_name', field_label: 'First Name', field_type: 'text', field_options: [], required: true },
  { field_key: 'last_name', field_label: 'Last Name', field_type: 'text', field_options: [], required: true },
  { field_key: 'email', field_label: 'Email Address', field_type: 'email', field_options: [], required: true },
  { field_key: 'phone', field_label: 'Phone Number', field_type: 'phone', field_options: [], required: true },
];

const PROFIT_CENTERS = [
  { key: 'memberships', label: 'Memberships' },
  { key: 'events', label: 'Events' },
  { key: 'sponsorships', label: 'Sponsorships' },
  { key: 'programming', label: 'Programming' },
  { key: 'proshop', label: 'Pro Shop' },
  { key: 'custom', label: 'Custom' },
];

const STATUS_COLORS = {
  planning:  { bg: 'rgba(255,255,255,0.08)', text: '#aaa',    label: 'Planning' },
  active:    { bg: 'rgba(34,197,94,0.15)',   text: '#22c55e', label: 'Active' },
  closed:    { bg: 'rgba(255,255,255,0.06)', text: '#666',    label: 'Closed' },
  cancelled: { bg: 'rgba(239,68,68,0.15)',   text: '#ef4444', label: 'Cancelled' },
};

const s = {
  module:       { padding: '24px', maxWidth: '1100px' },
  header:       { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' },
  title:        { fontSize: '22px', fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.3px' },
  subtitle:     { fontSize: '13px', color: '#666', marginTop: '2px' },
  btnYellow:    { background: 'var(--yellow)', color: '#000', border: 'none', borderRadius: '8px', padding: '9px 18px', fontWeight: 700, fontSize: '13px', cursor: 'pointer' },
  btnGhost:     { background: 'transparent', color: '#aaa', border: '1px solid var(--border)', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer' },
  btnSmall:     { background: 'transparent', border: '1px solid var(--border)', borderRadius: '6px', color: '#aaa', padding: '6px 10px', fontSize: '12px', cursor: 'pointer' },
  btnSmallDanger: { background: 'transparent', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '6px', color: '#ef4444', padding: '6px 10px', fontSize: '12px', cursor: 'pointer' },
  btnDanger:    { background: 'rgba(239,68,68,0.12)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer' },
  kpiRow:       { display: 'flex', gap: '12px', marginBottom: '24px' },
  kpiCard:      { flex: 1, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: '10px', padding: '16px 20px' },
  kpiVal:       { fontSize: '28px', fontWeight: 700, color: 'var(--text)' },
  kpiLabel:     { fontSize: '11px', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' },
  filterRow:    { display: 'flex', gap: '10px', marginBottom: '18px', alignItems: 'center' },
  filterSelect: { background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', padding: '8px 12px', fontSize: '13px', cursor: 'pointer' },
  card:         { background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: '12px', padding: '20px', marginBottom: '12px', cursor: 'pointer' },
  cardHeader:   { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' },
  cardTitle:    { fontSize: '16px', fontWeight: 700, color: 'var(--text)' },
  badge:        { borderRadius: '6px', padding: '3px 10px', fontSize: '11px', fontWeight: 700 },
  cardMeta:     { display: 'flex', gap: '16px', marginTop: '10px', flexWrap: 'wrap' },
  metaItem:     { fontSize: '12px', color: '#888' },
  metaVal:      { color: 'var(--text)', fontWeight: 600 },
  statsRow:     { display: 'flex', gap: '20px', marginTop: '14px', paddingTop: '14px', borderTop: '1px solid var(--border)', flexWrap: 'wrap' },
  statItem:     { fontSize: '12px', color: '#888' },
  statVal:      { fontSize: '20px', fontWeight: 700, color: 'var(--text)', display: 'block' },
  empty:        { textAlign: 'center', padding: '60px 20px', color: '#555' },
  modal:        { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '40px 20px' },
  modalBox:     { background: '#111', border: '1px solid var(--border)', borderRadius: '16px', width: '100%', maxWidth: '700px', padding: '32px' },
  modalTitle:   { fontSize: '20px', fontWeight: 700, color: 'var(--text)', marginBottom: '24px' },
  section:      { marginBottom: '24px' },
  sectionLabel: { fontSize: '11px', fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '12px' },
  row2:         { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' },
  row3:         { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px' },
  field:        { display: 'flex', flexDirection: 'column', gap: '6px' },
  label:        { fontSize: '12px', color: '#888', fontWeight: 600 },
  input:        { background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', padding: '9px 12px', fontSize: '14px', width: '100%', boxSizing: 'border-box' },
  textarea:     { background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', padding: '9px 12px', fontSize: '14px', width: '100%', boxSizing: 'border-box', resize: 'vertical', minHeight: '80px' },
  select:       { background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', padding: '9px 12px', fontSize: '14px', width: '100%', cursor: 'pointer' },
  checkRow:     { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  checkPill:    { border: '1px solid var(--border)', borderRadius: '20px', padding: '5px 12px', fontSize: '12px', cursor: 'pointer', transition: 'all 0.15s' },
  fieldCard:    { background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border)', borderRadius: '8px', padding: '14px', marginBottom: '8px' },
  goalCard:     { background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border)', borderRadius: '10px', padding: '16px', marginBottom: '10px' },
  goalHeader:   { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' },
  goalTitle:    { fontSize: '13px', fontWeight: 700, color: 'var(--text)' },
  urlBox:       { background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border)', borderRadius: '8px', padding: '12px 16px', fontSize: '13px', color: 'var(--yellow)', fontFamily: 'monospace', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px' },
  copyBtn:      { background: 'transparent', border: 'none', color: '#666', cursor: 'pointer', fontSize: '12px', padding: '2px 6px' },
  modalFooter:  { display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '28px', paddingTop: '20px', borderTop: '1px solid var(--border)' },
};

function GoalEditor({ goals, onChange }) {
  function addGoal(pcKey) {
    const existing = goals.find(g => g.profit_center === pcKey && pcKey !== 'programming' && pcKey !== 'custom');
    if (existing && pcKey !== 'programming' && pcKey !== 'custom') return;
    const newGoal = { profit_center: pcKey, label: '', goal_type: 'count', goal_amount: '', goal_trials: '', goal_pb101: '', goal_memberships: '', goal_private_events: '', goal_corporate_events: '', notes: '' };
    onChange([...goals, newGoal]);
  }

  function removeGoal(idx) {
    onChange(goals.filter((_, i) => i !== idx));
  }

  function updateGoal(idx, key, val) {
    const updated = [...goals];
    updated[idx] = { ...updated[idx], [key]: val };
    onChange(updated);
  }

  const activePCs = new Set(goals.map(g => g.profit_center));

  return (
    <div>
      {/* Profit center selector pills */}
      <div style={{ ...s.checkRow, marginBottom: '14px' }}>
        {PROFIT_CENTERS.map(pc => {
          const active = pc.key === 'programming' || pc.key === 'custom'
            ? false
            : activePCs.has(pc.key);
          return (
            <span
              key={pc.key}
              style={{ ...s.checkPill, background: active ? 'rgba(255,215,0,0.15)' : 'transparent', color: active ? 'var(--yellow)' : '#666', borderColor: active ? 'var(--yellow)' : 'var(--border)' }}
              onClick={() => {
                if (active) {
                  onChange(goals.filter(g => g.profit_center !== pc.key));
                } else {
                  addGoal(pc.key);
                }
              }}
            >
              {pc.key === 'programming' || pc.key === 'custom' ? `+ ${pc.label}` : pc.label}
            </span>
          );
        })}
      </div>

      {/* Goal cards */}
      {goals.map((goal, idx) => (
        <div key={idx} style={s.goalCard}>
          <div style={s.goalHeader}>
            <span style={s.goalTitle}>
              {goal.profit_center === 'custom' || goal.profit_center === 'programming'
                ? PROFIT_CENTERS.find(p => p.key === goal.profit_center)?.label
                : PROFIT_CENTERS.find(p => p.key === goal.profit_center)?.label}
            </span>
            <button style={s.btnSmallDanger} onClick={() => removeGoal(idx)}>Remove</button>
          </div>

          {/* Memberships */}
          {goal.profit_center === 'memberships' && (
            <div style={s.row3}>
              <div style={s.field}>
                <label style={s.label}>Trial Goal</label>
                <input className="field-input" type="number" placeholder="10" value={goal.goal_trials} onChange={e => updateGoal(idx, 'goal_trials', e.target.value)} style={{ fontSize: 13, padding: '6px 10px' }} />
              </div>
              <div style={s.field}>
                <label style={s.label}>PB101 Goal</label>
                <input className="field-input" type="number" placeholder="5" value={goal.goal_pb101} onChange={e => updateGoal(idx, 'goal_pb101', e.target.value)} style={{ fontSize: 13, padding: '6px 10px' }} />
              </div>
              <div style={s.field}>
                <label style={s.label}>Membership Goal</label>
                <input className="field-input" type="number" placeholder="3" value={goal.goal_memberships} onChange={e => updateGoal(idx, 'goal_memberships', e.target.value)} style={{ fontSize: 13, padding: '6px 10px' }} />
              </div>
            </div>
          )}

          {/* Events */}
          {goal.profit_center === 'events' && (
            <div style={s.row3}>
              <div style={s.field}>
                <label style={s.label}>Private Events Goal</label>
                <input className="field-input" type="number" placeholder="2" value={goal.goal_private_events} onChange={e => updateGoal(idx, 'goal_private_events', e.target.value)} style={{ fontSize: 13, padding: '6px 10px' }} />
              </div>
              <div style={s.field}>
                <label style={s.label}>Corporate Events Goal</label>
                <input className="field-input" type="number" placeholder="1" value={goal.goal_corporate_events} onChange={e => updateGoal(idx, 'goal_corporate_events', e.target.value)} style={{ fontSize: 13, padding: '6px 10px' }} />
              </div>
              <div style={s.field}>
                <label style={s.label}>Revenue Goal ($)</label>
                <input className="field-input" type="number" placeholder="2500" value={goal.goal_amount} onChange={e => updateGoal(idx, 'goal_amount', e.target.value)} style={{ fontSize: 13, padding: '6px 10px' }} />
              </div>
            </div>
          )}

          {/* Sponsorships */}
          {goal.profit_center === 'sponsorships' && (
            <div style={s.row2}>
              <div style={s.field}>
                <label style={s.label}>Revenue Goal ($)</label>
                <input className="field-input" type="number" placeholder="5000" value={goal.goal_amount} onChange={e => updateGoal(idx, 'goal_amount', e.target.value)} style={{ fontSize: 13, padding: '6px 10px' }} />
              </div>
              <div style={s.field}>
                <label style={s.label}>Notes</label>
                <input className="field-input" type="text" placeholder="e.g. 2 hole sponsors @ $500 each" value={goal.notes} onChange={e => updateGoal(idx, 'notes', e.target.value)} style={{ fontSize: 13, padding: '6px 10px' }} />
              </div>
            </div>
          )}

          {/* Programming */}
          {goal.profit_center === 'programming' && (
            <div style={s.row2}>
              <div style={s.field}>
                <label style={s.label}>Program Name</label>
                <input className="field-input" type="text" placeholder="e.g. Summer Junior Camp" value={goal.label} onChange={e => updateGoal(idx, 'label', e.target.value)} style={{ fontSize: 13, padding: '6px 10px' }} />
              </div>
              <div style={s.field}>
                <label style={s.label}>Registration Goal</label>
                <input className="field-input" type="number" placeholder="20" value={goal.goal_amount} onChange={e => updateGoal(idx, 'goal_amount', e.target.value)} style={{ fontSize: 13, padding: '6px 10px' }} />
              </div>
            </div>
          )}

          {/* Pro Shop */}
          {goal.profit_center === 'proshop' && (
            <div style={{ maxWidth: '220px' }}>
              <div style={s.field}>
                <label style={s.label}>Revenue Goal ($)</label>
                <input className="field-input" type="number" placeholder="500" value={goal.goal_amount} onChange={e => updateGoal(idx, 'goal_amount', e.target.value)} style={{ fontSize: 13, padding: '6px 10px' }} />
              </div>
            </div>
          )}

          {/* Custom */}
          {goal.profit_center === 'custom' && (
            <div style={s.row3}>
              <div style={s.field}>
                <label style={s.label}>Goal Name</label>
                <input className="field-input" type="text" placeholder="e.g. Day Passes Redeemed" value={goal.label} onChange={e => updateGoal(idx, 'label', e.target.value)} style={{ fontSize: 13, padding: '6px 10px' }} />
              </div>
              <div style={s.field}>
                <label style={s.label}>Type</label>
                <select className="field-input" value={goal.goal_type} onChange={e => updateGoal(idx, 'goal_type', e.target.value)} style={{ fontSize: 13, padding: '6px 10px' }}>
                  <option value="count">Count (#)</option>
                  <option value="revenue">Revenue ($)</option>
                </select>
              </div>
              <div style={s.field}>
                <label style={s.label}>Target</label>
                <input className="field-input" type="number" placeholder="50" value={goal.goal_amount} onChange={e => updateGoal(idx, 'goal_amount', e.target.value)} style={{ fontSize: 13, padding: '6px 10px' }} />
              </div>
            </div>
          )}
        </div>
      ))}

      {goals.length === 0 && (
        <div style={{ textAlign: 'center', padding: '16px', color: '#444', fontSize: '13px', border: '1px dashed var(--border)', borderRadius: '8px' }}>
          Select a profit center above to add goals for this activation.
        </div>
      )}
    </div>
  );
}

// ─── Incentive / Offer editor — repeatable list, mirrors GoalEditor's shape ──
// Sprint 19.9: was a single offer/offer_label pair on the activation itself;
// Amy's request was the ability to show more than one offer on the public
// form (e.g. "Win a Free Trial OR a Private Event"). Each entry here maps to
// one row in the new activation_offers table.
function OfferEditor({ offers, onChange }) {
  function addOffer() {
    onChange([...offers, { offer_label: '', offer_text: '' }]);
  }

  function removeOffer(idx) {
    onChange(offers.filter((_, i) => i !== idx));
  }

  function updateOffer(idx, key, val) {
    const updated = [...offers];
    updated[idx] = { ...updated[idx], [key]: val };
    onChange(updated);
  }

  return (
    <div>
      {offers.map((offer, idx) => (
        <div key={idx} style={s.goalCard}>
          <div style={s.goalHeader}>
            <span style={s.goalTitle}>Offer {idx + 1}</span>
            <button style={s.btnSmallDanger} onClick={() => removeOffer(idx)}>Remove</button>
          </div>
          <div style={{ ...s.field, marginBottom: '10px' }}>
            <label style={s.label}>Offer Label <span style={{ color: '#555', fontWeight: 400 }}>(e.g. "Event Special", "Member Promotion", "Summer Offer")</span></label>
            <input className="field-input" style={{ fontSize: 13, padding: '6px 10px' }} placeholder="Event Special" value={offer.offer_label || ''} onChange={e => updateOffer(idx, 'offer_label', e.target.value)} />
          </div>
          <div style={s.field}>
            <label style={s.label}>Incentive / Offer</label>
            <input className="field-input" style={{ fontSize: 13, padding: '6px 10px' }} placeholder="e.g. Free Day Pass on form completion" value={offer.offer_text || ''} onChange={e => updateOffer(idx, 'offer_text', e.target.value)} />
          </div>
        </div>
      ))}

      {offers.length === 0 && (
        <div style={{ textAlign: 'center', padding: '16px', color: '#444', fontSize: '13px', border: '1px dashed var(--border)', borderRadius: '8px', marginBottom: '10px' }}>
          No offers yet — click "+ Add Incentive/Offer" below.
        </div>
      )}

      <button
        style={{ ...s.btnSmall, color: 'var(--yellow)', borderColor: 'rgba(255,215,0,0.3)' }}
        onClick={addOffer}
      >+ Add Incentive/Offer</button>
    </div>
  );
}

function ActivationsModule() {
  const { get, post, put } = useApi();
  const { getAccessTokenSilently } = useAuth0();
  const { role } = useAuth();
  const [activations, setActivations] = useState([]);
  const [overview, setOverview] = useState(null);
  const [activationTypes, setActivationTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  // Club filter goes to the backend (already supported by GET /activations,
  // just never exposed here). Type/month/search/sort stay client-side —
  // realistic data volume here is dozens of rows, not thousands, so a
  // second server-side filtering path isn't worth the complexity. See
  // docs/COMS_Activation_Builder_Cleanup_Spec.md.
  const [stateFilter, setStateFilter] = useState(''); // '' | 'TX' | 'WI' — client-side, no backend support for this grouping
  const [locationFilter, setLocationFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState(''); // 'YYYY-MM' or ''
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('default'); // default | created | leads | name
  const [showModal, setShowModal] = useState(false);
  const [editingActivation, setEditingActivation] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showTemplateMenu, setShowTemplateMenu] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [qrActivation, setQrActivation] = useState(null);

  async function deleteActivation(e, a) {
    e.stopPropagation();
    if (!window.confirm(`Delete "${a.name}"? This cannot be undone.`)) return;
    const API_BASE = process.env.REACT_APP_API_BASE || 'https://murko-coms-api.azurewebsites.net';
    try {
      const token = await getAccessTokenSilently();
      const res = await fetch(`${API_BASE}/api/activations/${a.activation_id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.message || 'Delete failed. Please try again.');
        return;
      }
      setActivations(prev => prev.filter(x => x.activation_id !== a.activation_id));
    } catch (err) {
      alert('Delete failed. Please try again.');
    }
  }

  const defaultForm = {
    name: '', type: '', primary_location_id: '', additional_location_ids: [],
    event_date: '', end_date: '', status: 'planning', capture_methods: [],
    offer: '', offer_label: '', notes: '', owner_email: '', redirect_url: '', form_disclaimer: '', source: '', form_intro: '', fields: [], goals: [], offers: [],
    partner_logo_1_url: '', partner_logo_2_url: '', submit_label: '', form_disclaimer_position: 'above', form_disclaimer_style: 'blue_italic', sms_consent_required: true, tagline: '', tagline_hidden: false,
  };
  const [form, setForm] = useState(defaultForm);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (statusFilter) qs.set('status', statusFilter);
      if (locationFilter) qs.set('location_id', locationFilter);
      const params = qs.toString() ? `?${qs.toString()}` : '';
      const [acts, ov, types] = await Promise.allSettled([
        get(`/activations${params}`),
        get('/activations/overview'),
        get('/activation-types'),
      ]);
      if (acts.status === 'fulfilled') setActivations(Array.isArray(acts.value) ? acts.value : []);
      if (ov.status === 'fulfilled') setOverview(ov.value || null);
      if (types.status === 'fulfilled') setActivationTypes(Array.isArray(types.value) ? types.value : []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, locationFilter]);

  useEffect(() => { load(); }, [load]);

  function openNew() {
    setEditingActivation(null);
    setForm({ ...defaultForm, fields: DEFAULT_FIELDS.map(f => ({...f})) });
    setShowModal(true);
  }

  async function openEdit(a) {
    try {
      const full = await get(`/activations/${a.activation_id}`);
      const fields = full.fields || [];
      setEditingActivation(full);
      setForm({
        name: full.name || '',
        type: full.type || '',
        primary_location_id: full.primary_location_id || '',
        additional_location_ids: full.additional_location_ids || [],
        event_date: full.event_date ? full.event_date.slice(0, 10) : '',
        end_date: full.end_date ? full.end_date.slice(0, 10) : '',
        status: full.status || 'planning',
        capture_methods: full.capture_methods || [],
        offer: full.offer || '',
        offer_label: full.offer_label || '',
        notes: full.notes || '',
        owner_email: full.owner_email || '',
        redirect_url: full.redirect_url || '',
        form_disclaimer: full.form_disclaimer || '',
        form_intro: full.form_intro || '',
        source: full.source || '',
        fields: fields.length > 0 ? fields : DEFAULT_FIELDS.map(f => ({...f})),
        goals: full.goals || [],
        offers: full.offers || [],
        partner_logo_1_url: full.partner_logo_1_url || '',
        partner_logo_2_url: full.partner_logo_2_url || '',
        submit_label: full.submit_label || '',
        form_disclaimer_position: full.form_disclaimer_position || 'above',
        form_disclaimer_style: full.form_disclaimer_style || 'blue_italic',
        sms_consent_required: full.sms_consent_required !== false,
        tagline: full.tagline || '',
        tagline_hidden: !!full.tagline_hidden,
      });
    } catch (e) {
      setEditingActivation(a);
      setForm({
        name: a.name || '',
        type: a.type || '',
        primary_location_id: a.primary_location_id || '',
        additional_location_ids: a.additional_location_ids || [],
        event_date: a.event_date ? a.event_date.slice(0, 10) : '',
        end_date: a.end_date ? a.end_date.slice(0, 10) : '',
        status: a.status || 'planning',
        capture_methods: a.capture_methods || [],
        offer: a.offer || '',
        offer_label: a.offer_label || '',
        notes: a.notes || '',
        owner_email: a.owner_email || '',
        redirect_url: a.redirect_url || '',
        form_disclaimer: a.form_disclaimer || '',
        form_intro: a.form_intro || '',
        source: a.source || '',
        fields: DEFAULT_FIELDS.map(f => ({...f})),
        goals: a.goals || [],
        offers: a.offers || [],
        partner_logo_1_url: a.partner_logo_1_url || '',
        partner_logo_2_url: a.partner_logo_2_url || '',
        submit_label: a.submit_label || '',
        form_disclaimer_position: a.form_disclaimer_position || 'above',
        form_disclaimer_style: a.form_disclaimer_style || 'blue_italic',
        sms_consent_required: a.sms_consent_required !== false,
        tagline: a.tagline || '',
        tagline_hidden: !!a.tagline_hidden,
      });
    }
    setShowModal(true);
  }

  // Sprint 19.9: QR modal used to receive the plain list-row object, which
  // never carried `offers` (or `fields`/`goals`) since GET /activations
  // returns flat rows, not the joined detail. Fetch the real detail record
  // first (same pattern as openEdit) so the print/Word-doc offer block(s)
  // are always accurate, falling back to the list row if the fetch fails.
  async function openQr(a) {
    try {
      const full = await get(`/activations/${a.activation_id}`);
      setQrActivation(full);
    } catch (e) {
      setQrActivation(a);
    }
  }

  async function duplicateActivation(e, a) {
    e.stopPropagation();
    try {
      const full = await get(`/activations/${a.activation_id}`);
      // Open as new activation with all fields copied but reset key fields
      setEditingActivation(null);
      setForm({
        name: '',
        type: full.type || '',
        primary_location_id: full.primary_location_id || '',
        additional_location_ids: full.additional_location_ids || [],
        event_date: '',
        end_date: '',
        status: 'planning',
        capture_methods: full.capture_methods || [],
        offer: full.offer || '',
        offer_label: full.offer_label || '',
        notes: full.notes || '',
        owner_email: full.owner_email || '',
        redirect_url: '',
        form_disclaimer: full.form_disclaimer || '',
        form_intro: full.form_intro || '',
        source: full.source || '',
        fields: (full.fields || []).length > 0 ? full.fields.map(f => ({...f})) : DEFAULT_FIELDS.map(f => ({...f})),
        goals: (full.goals || []).map(g => ({...g})),
        offers: (full.offers || []).map(o => ({...o})),
        partner_logo_1_url: full.partner_logo_1_url || '',
        partner_logo_2_url: full.partner_logo_2_url || '',
        submit_label: full.submit_label || '',
        form_disclaimer_position: full.form_disclaimer_position || 'above',
        form_disclaimer_style: full.form_disclaimer_style || 'blue_italic',
        sms_consent_required: full.sms_consent_required !== false,
        tagline: full.tagline || '',
        tagline_hidden: !!full.tagline_hidden,
      });
      setShowModal(true);
    } catch (e) {
      console.error('Duplicate failed', e);
    }
  }

  async function save() {
    if (!form.name || !form.type || !form.primary_location_id) return;
    if (form.type === 'Ambassador' && !form.notes) return alert('Ambassador Name is required.');
    setSaving(true);
    try {
      const payload = { ...form };
      if (editingActivation) {
        await put(`/activations/${editingActivation.activation_id}`, payload);
      } else {
        await post('/activations', payload);
      }
      setShowModal(false);
      load();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  }

  function toggleCapture(method) {
    setForm(f => ({
      ...f,
      capture_methods: f.capture_methods.includes(method)
        ? f.capture_methods.filter(m => m !== method)
        : [...f.capture_methods, method],
    }));
  }

  function toggleAdditionalClub(id) {
    setForm(f => ({
      ...f,
      additional_location_ids: f.additional_location_ids.includes(id)
        ? f.additional_location_ids.filter(c => c !== id)
        : [...f.additional_location_ids, id],
    }));
  }

  function addFieldFromTemplate(template) {
    setForm(f => {
      if (f.fields.some(fl => fl.field_key === template.field_key)) return f;
      const newField = {
        field_key: template.field_key,
        field_label: template.label,
        field_type: template.field_type,
        field_options: [...(template.field_options || [])],
        required: template.required || false,
      };
      return { ...f, fields: [...f.fields, newField] };
    });
    setShowTemplateMenu(false);
  }

  function addCustomField() {
    setForm(f => ({
      ...f,
      fields: [...f.fields, {
        field_key: `custom_${Date.now()}`,
        field_label: '',
        field_type: 'text',
        field_options: [],
        required: false,
        isNew: true,
      }],
    }));
    setShowTemplateMenu(false);
  }

  function updateField(idx, key, val) {
    setForm(f => {
      const fields = [...f.fields];
      fields[idx] = { ...fields[idx], [key]: val, isNew: false };
      // Auto-generate field_key for custom fields only
      if (key === 'field_label' && fields[idx].field_key.startsWith('custom_')) {
        const slug = val.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/(^_|_$)/g, '');
        fields[idx].field_key = slug ? `custom_${slug}` : fields[idx].field_key;
      }
      return { ...f, fields };
    });
  }

  function removeField(idx) {
    setForm(f => ({ ...f, fields: f.fields.filter((_, i) => i !== idx) }));
  }

  function moveField(idx, dir) {
    setForm(f => {
      const fields = [...f.fields];
      const swap = idx + dir;
      if (swap < 0 || swap >= fields.length) return f;
      [fields[idx], fields[swap]] = [fields[swap], fields[idx]];
      return { ...f, fields };
    });
  }

  function copyUrl(activationId) {
    const url = `https://www.playpicklr.com/${activationId}`;
    navigator.clipboard.writeText(url).catch(() => {});
    setCopiedId(activationId);
    setTimeout(() => setCopiedId(null), 2000);
  }

  const formUrlPreview = form.name
    ? form.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '').slice(0, 50)
    : null;

  // Client-side type/month/search/sort — status + club already filter at the
  // backend (see load() above). Month filter deliberately never hides
  // undated (evergreen) activations — an activation with no event_date
  // isn't tied to any month, so filtering by one shouldn't make it
  // disappear (the exact "where did it go" problem that prompted this
  // whole cleanup pass in the first place).
  const visibleActivations = useMemo(() => {
    let list = activations;

    // State groups clubs (TX: MCK/LEW/DEN, WI: WA/MF/GB) — an activation
    // counts as being "in" a state if any club it touches (primary or
    // additional) is there, same "touches this club at all" logic the
    // backend's own location_id filter already uses.
    if (stateFilter) {
      list = list.filter(a =>
        CLUB_STATE[a.primary_location_id] === stateFilter ||
        (a.additional_location_ids || []).some(id => CLUB_STATE[id] === stateFilter)
      );
    }

    if (typeFilter) list = list.filter(a => a.type === typeFilter);

    if (monthFilter) {
      list = list.filter(a => !a.event_date || String(a.event_date).slice(0, 7) === monthFilter);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter(a =>
        (a.name || '').toLowerCase().includes(q) ||
        (a.type || '').toLowerCase().includes(q) ||
        (a.notes || '').toLowerCase().includes(q)
      );
    }

    if (sortBy !== 'default') {
      list = [...list].sort((a, b) => {
        if (sortBy === 'created') return new Date(b.created_at) - new Date(a.created_at);
        if (sortBy === 'leads') return (b.lead_count ?? 0) - (a.lead_count ?? 0);
        if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
        return 0;
      });
    }

    return list;
  }, [activations, stateFilter, typeFilter, monthFilter, searchQuery, sortBy]);

  if (!['admin', 'mloc'].includes(role)) {
    return <div style={s.module}><p style={{ color: '#666' }}>Access restricted.</p></div>;
  }

  return (
    <div style={s.module}>
      {/* Header */}
      <div style={s.header}>
        <div>
          <div style={s.title}>Activations</div>
          <div style={s.subtitle}>Lead capture campaigns, events, and activations</div>
        </div>
        <button style={s.btnYellow} onClick={openNew}>+ New Activation</button>
      </div>

      {/* KPI Row */}
      {overview && (
        <div style={s.kpiRow}>
          {[
            { val: overview.active_activations,  label: 'Active' },
            { val: overview.total_leads,          label: 'Total Leads' },
            { val: overview.leads_this_week,      label: 'Leads This Week' },
            { val: overview.total_promoted,       label: 'Promoted to Pipeline' },
          ].map(k => (
            <div key={k.label} style={s.kpiCard}>
              <div style={s.kpiVal}>{k.val ?? '—'}</div>
              <div style={s.kpiLabel}>{k.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div style={{ ...s.filterRow, flexWrap: 'wrap' }}>
        <select style={s.filterSelect} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="planning">Planning</option>
          <option value="active">Active</option>
          <option value="closed">Closed</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select
          style={s.filterSelect}
          value={stateFilter}
          onChange={e => {
            const next = e.target.value;
            setStateFilter(next);
            // Clear an out-of-state club selection rather than leaving a
            // contradictory (state=WI, club=McKinney) combination in place.
            if (next && locationFilter && CLUB_STATE[locationFilter] !== next) setLocationFilter('');
          }}
        >
          <option value="">All States</option>
          <option value="WI">Wisconsin</option>
          <option value="TX">Texas</option>
        </select>
        <select style={s.filterSelect} value={locationFilter} onChange={e => setLocationFilter(e.target.value)}>
          <option value="">All Clubs</option>
          {CLUBS.filter(c => !stateFilter || CLUB_STATE[c.id] === stateFilter).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select style={s.filterSelect} value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
          <option value="">All Types</option>
          {activationTypes.map(t => <option key={t.type_id} value={t.name}>{t.name}</option>)}
        </select>
        <input
          type="month"
          style={s.filterSelect}
          value={monthFilter}
          onChange={e => setMonthFilter(e.target.value)}
          title="Filter by event month — activations with no fixed date always stay visible"
        />
        <input
          type="text"
          placeholder="Search name, type, notes…"
          style={{ ...s.filterSelect, cursor: 'text', minWidth: '200px' }}
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
        <select style={s.filterSelect} value={sortBy} onChange={e => setSortBy(e.target.value)}>
          <option value="default">Sort: Event Date</option>
          <option value="created">Sort: Created Date</option>
          <option value="leads">Sort: Most Leads</option>
          <option value="name">Sort: Name A–Z</option>
        </select>
        <span style={{ fontSize: '13px', color: '#555', marginLeft: '4px' }}>
          {visibleActivations.length} of {activations.length} activation{activations.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Cards */}
      {loading ? (
        <div style={s.empty}>Loading activations...</div>
      ) : activations.length === 0 ? (
        <div style={s.empty}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>📋</div>
          <div style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text)', marginBottom: '6px' }}>No activations yet</div>
          <div style={{ fontSize: '13px' }}>Create your first activation to start capturing leads at events and campaigns.</div>
          <button style={{ ...s.btnYellow, marginTop: '20px' }} onClick={openNew}>+ New Activation</button>
        </div>
      ) : visibleActivations.length === 0 ? (
        <div style={s.empty}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>🔍</div>
          <div style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text)', marginBottom: '6px' }}>No activations match your filters</div>
          <div style={{ fontSize: '13px' }}>Try clearing the type, month, or search filters above.</div>
        </div>
      ) : (
        visibleActivations.map(a => {
          const sc = STATUS_COLORS[a.status] || STATUS_COLORS.planning;
          const club = CLUBS.find(c => c.id === a.primary_location_id);
          const additionalClubs = (a.additional_location_ids || []).map(id => CLUBS.find(c => c.id === id)?.name).filter(Boolean);
          return (
            <div key={a.activation_id} style={s.card} onClick={() => openEdit(a)}>
              <div style={s.cardHeader}>
                <div>
                  <div style={s.cardTitle}>{a.name}</div>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '3px' }}>{a.type}</div>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span style={{ ...s.badge, background: sc.bg, color: sc.text }}>{sc.label}</span>
                <div style={{ display: 'flex', gap: '6px' }} onClick={e => e.stopPropagation()}>
                    {a.status === 'active' && (
                      <button style={{ ...s.btnGhost, fontSize: '11px', padding: '4px 10px' }}
                        onClick={() => copyUrl(a.activation_id)}>
                        {copiedId === a.activation_id ? '✓ Copied' : '🔗 Copy URL'}
                      </button>
                    )}
                    <button style={{ ...s.btnGhost, fontSize: '11px', padding: '4px 10px', color: 'var(--yellow)', borderColor: 'rgba(255,215,0,0.3)' }}
                      onClick={() => openQr(a)}>
                      📱 QR + Print
                    </button>
                    <button style={{ ...s.btnGhost, fontSize: '11px', padding: '4px 10px', color: '#a855f7', borderColor: 'rgba(168,85,247,0.3)' }}
                      onClick={(e) => duplicateActivation(e, a)}>
                      ⧉ Duplicate
                    </button>
                    {(a.lead_count ?? 0) > 0 && (
                      <button style={{ ...s.btnGhost, fontSize: '11px', padding: '4px 10px', color: '#22c55e', borderColor: 'rgba(34,197,94,0.3)' }}
                        onClick={async (e) => {
                          e.stopPropagation();
                          const API_BASE = process.env.REACT_APP_API_BASE || 'https://murko-coms-api.azurewebsites.net';
                          try {
                            const token = await getAccessTokenSilently();
                            const res = await fetch(`${API_BASE}/api/activations/${a.activation_id}/leads/export`, {
                              headers: { Authorization: `Bearer ${token}` },
                            });
                            if (!res.ok) throw new Error('Export failed');
                            const blob = await res.blob();
                            const url = URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = `${a.activation_id}-leads.csv`;
                            link.click();
                            URL.revokeObjectURL(url);
                          } catch (err) {
                            alert('Download failed. Please try again.');
                          }
                        }}>
                        ⬇ Download responses
                      </button>
                    )}
                    {['admin','mloc'].includes(role) && (
                      <button style={{ ...s.btnGhost, fontSize: '11px', padding: '4px 10px', color: '#ef4444', borderColor: 'rgba(239,68,68,0.3)' }}
                        onClick={(e) => deleteActivation(e, a)}>
                        🗑 Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div style={s.cardMeta}>
                <span style={s.metaItem}>
                  Club: <span style={s.metaVal}>{club?.name || a.primary_location_id}</span>
                  {additionalClubs.length > 0 && <span style={{ color: '#555' }}> + {additionalClubs.join(', ')}</span>}
                </span>
                {a.type && <span style={s.metaItem}>Type: <span style={s.metaVal}>{a.type}</span></span>}
                {a.event_date && (
                  <span style={s.metaItem}>
                    Date: <span style={s.metaVal}>{new Date(a.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}</span>
                  </span>
                )}
                {(a.offers || []).length > 0 && (
                  <span style={s.metaItem}>
                    {a.offers.length === 1 ? 'Offer: ' : `Offers (${a.offers.length}): `}
                    <span style={s.metaVal}>{a.offers[0].offer_label || a.offers[0].offer_text}</span>
                    {a.offers.length > 1 && <span style={{ color: '#555' }}> +{a.offers.length - 1} more</span>}
                  </span>
                )}
                {!(a.offers || []).length && a.offer && <span style={s.metaItem}>Offer: <span style={s.metaVal}>{a.offer}</span></span>}
                {(a.capture_methods || []).length > 0 && <span style={s.metaItem}>Capture: <span style={s.metaVal}>{a.capture_methods.join(', ')}</span></span>}
              </div>
              <div style={s.statsRow}>
                <div style={s.statItem}><span style={s.statVal}>{a.lead_count ?? 0}</span>Leads captured</div>
                <div style={s.statItem}><span style={s.statVal}>{a.promoted_count ?? 0}</span>Promoted to pipeline</div>
                {(a.fields || []).length > 0 && <div style={s.statItem}><span style={s.statVal}>{a.fields?.length ?? 0}</span>Form fields</div>}
              </div>
              {a.status === 'active' && (
                <div style={{ marginTop: '12px' }}>
                  <div style={{ fontSize: '11px', color: '#555', marginBottom: '4px' }}>Form URL</div>
                  <div style={{ ...s.urlBox, padding: '8px 12px', fontSize: '12px' }}>
                    <span>playpicklr.com/{a.activation_id}</span>
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}

      {/* Modal */}
      {showModal && (
        <div style={s.modal} onClick={e => { if (e.target === e.currentTarget) setShowModal(false); }}>
          <div style={s.modalBox}>
            <div style={s.modalTitle}>{editingActivation ? 'Edit Activation' : 'New Activation'}</div>

            {/* Basic Info */}
            <div style={s.section}>
              <div style={s.sectionLabel}>Basic Info</div>
              <div style={{ ...s.field, marginBottom: '14px' }}>
                <label style={s.label}>Activation Name *</label>
                <input className="field-input" style={{ fontSize: 14, padding: '8px 12px' }} placeholder="e.g. Bay Port Golf Outing 2026" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div style={s.row2}>
                <div style={s.field}>
                  <label style={s.label}>Type *</label>
                  <select className="field-input" style={{ fontSize: 14, padding: '8px 12px' }} value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                    <option value="">Select type...</option>
                    {activationTypes.map(t => <option key={t.type_id} value={t.name}>{t.name}</option>)}
                  </select>
                </div>
                <div style={s.field}>
                  <label style={s.label}>Status</label>
                  <select className="field-input" style={{ fontSize: 14, padding: '8px 12px' }} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                    <option value="planning">Planning</option>
                    <option value="active">Active</option>
                    <option value="closed">Closed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Club Assignment */}
            <div style={s.section}>
              <div style={s.sectionLabel}>Club Assignment</div>
              <div style={s.field}>
                <label style={s.label}>Primary Club *</label>
                <select className="field-input" style={{ fontSize: 14, padding: '8px 12px' }} value={form.primary_location_id} onChange={e => setForm(f => ({ ...f, primary_location_id: e.target.value }))}>
                  <option value="">Select club...</option>
                  {CLUBS.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div style={{ marginTop: '12px' }}>
                <label style={s.label}>Also Serving (optional)</label>
                <div style={{ ...s.checkRow, marginTop: '8px' }}>
                  {CLUBS.filter(c => c.id !== form.primary_location_id).map(c => {
                    const selected = form.additional_location_ids.includes(c.id);
                    return (
                      <span key={c.id} style={{ ...s.checkPill, background: selected ? 'rgba(255,215,0,0.15)' : 'transparent', color: selected ? 'var(--yellow)' : '#666', borderColor: selected ? 'var(--yellow)' : 'var(--border)' }} onClick={() => toggleAdditionalClub(c.id)}>
                        {c.name}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Dates */}
            <div style={s.section}>
              <div style={s.sectionLabel}>Dates</div>
              <div style={s.row2}>
                <div style={s.field}>
                  <label style={s.label}>Event Date</label>
                  <input className="field-input" type="date" value={form.event_date} onChange={e => setForm(f => ({ ...f, event_date: e.target.value }))} style={{ fontSize: 13, padding: '7px 10px' }} />
                </div>
                <div style={s.field}>
                  <label style={s.label}>End Date (if multi-day)</label>
                  <input className="field-input" type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} style={{ fontSize: 13, padding: '7px 10px' }} />
                </div>
              </div>
            </div>

            {/* Goals */}
            <div style={s.section}>
              <div style={s.sectionLabel}>Goals by Profit Center</div>
              <GoalEditor goals={form.goals} onChange={goals => setForm(f => ({ ...f, goals }))} />
            </div>

            {/* Offer + Capture */}
            <div style={s.section}>
              <div style={s.sectionLabel}>Offer & Capture</div>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ ...s.label, display: 'block', marginBottom: '8px' }}>
                  Incentives / Offers <span style={{ color: '#555', fontWeight: 400 }}>(shown on the public form — add more than one to offer a choice)</span>
                </label>
                <OfferEditor offers={form.offers} onChange={offers => setForm(f => ({ ...f, offers }))} />
              </div>
              <div style={{ marginBottom: '14px' }}>
                <label style={s.label}>Capture Methods</label>
                <div style={{ ...s.checkRow, marginTop: '8px' }}>
                  {CAPTURE_METHODS.map(m => {
                    const selected = form.capture_methods.includes(m);
                    return (
                      <span key={m} style={{ ...s.checkPill, background: selected ? 'rgba(255,215,0,0.15)' : 'transparent', color: selected ? 'var(--yellow)' : '#666', borderColor: selected ? 'var(--yellow)' : 'var(--border)' }} onClick={() => toggleCapture(m)}>
                        {m}
                      </span>
                    );
                  })}
                </div>
              </div>
              <div style={{ ...s.field, marginBottom: '14px' }}>
                <label style={s.label}>Owner Email</label>
                <input className="field-input" style={{ fontSize: 14, padding: '8px 12px' }} placeholder="aguerrero@murkocapital.com" value={form.owner_email} onChange={e => setForm(f => ({ ...f, owner_email: e.target.value }))} />
              </div>
              <div style={s.field}>
                <label style={s.label}>
                  {form.type === 'Ambassador' ? 'Ambassador Name' : 'Notes'}
                  {form.type === 'Ambassador' && <span style={{ color: '#ef4444', marginLeft: 4 }}>*</span>}
                </label>
                {form.type === 'Ambassador' ? (
                  <input
                    className="field-input"
                    style={{ fontSize: 14, padding: '8px 12px' }}
                    placeholder="e.g. CJ Singleton"
                    value={form.notes}
                    onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                    required
                  />
                ) : (
                  <textarea className="field-input" style={{ fontSize: 13, padding: '8px 12px', minHeight: '72px', resize: 'vertical' }} placeholder="Internal notes about this activation..." value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
                )}
              </div>
              <div style={s.field}>
                <label style={s.label}>Form Intro Text <span style={{ color: '#555', fontWeight: 400 }}>(optional — overrides the default intro from activation type)</span></label>
                <input className="field-input" style={{ fontSize: 14, padding: '8px 12px' }} placeholder="e.g. You found us online — welcome!" value={form.form_intro || ''} onChange={e => setForm(f => ({ ...f, form_intro: e.target.value }))} />
              </div>
              <div style={s.field}>
                <label style={s.label}>Tagline <span style={{ color: '#555', fontWeight: 400 }}>(the line under the event name on the form and printed tent; blank uses the standard line)</span></label>
                <input className="field-input" style={{ fontSize: 14, padding: '8px 12px', opacity: form.tagline_hidden ? 0.4 : 1 }}
                  placeholder="Please fill out the below information to receive updates about our club."
                  disabled={!!form.tagline_hidden} maxLength={200}
                  value={form.tagline || ''} onChange={e => setForm(f => ({ ...f, tagline: e.target.value }))} />
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 12, color: '#aaa', cursor: 'pointer' }}>
                  <input type="checkbox" style={{ accentColor: 'var(--yellow)' }} checked={!!form.tagline_hidden}
                    onChange={e => setForm(f => ({ ...f, tagline_hidden: e.target.checked }))} />
                  Hide the tagline
                </label>
              </div>
              <div style={s.field}>
                <label style={s.label}>Source Tag <span style={{ color: '#555', fontWeight: 400 }}>(optional — used for lead attribution on PlayPicklr forms)</span></label>
                <select className="field-input" style={{ fontSize: 14, padding: '8px 12px' }} value={form.source || ''} onChange={e => setForm(f => ({ ...f, source: e.target.value }))}>
                  <option value="">— None / General —</option>
                  <option value="playpicklr_form">playpicklr_form</option>
                  <option value="summer_slam_email">summer_slam_email</option>
                  <option value="summer_slam_meta">summer_slam_meta</option>
                  <option value="summer_slam_print">summer_slam_print</option>
                  <option value="summer_slam_community">summer_slam_community</option>
                  <option value="qr_physical">qr_physical</option>
                  <option value="qr_event">qr_event</option>
                  <option value="activation_waiver">activation_waiver</option>
                  <option value="corporate_event">corporate_event</option>
                </select>
              </div>
              <PartnerLogoField
                label="Partner Logo 1"
                hint="optional — shown next to the main brand mark on the RSVP form and the printed QR tent"
                labelStyle={s.label} fieldStyle={s.field}
                value={form.partner_logo_1_url || ''}
                onChange={url => setForm(f => ({ ...f, partner_logo_1_url: url }))} />
              <PartnerLogoField
                label="Partner Logo 2"
                hint="optional"
                labelStyle={s.label} fieldStyle={s.field}
                value={form.partner_logo_2_url || ''}
                onChange={url => setForm(f => ({ ...f, partner_logo_2_url: url }))} />
            </div>

            {/* Form Builder */}
            <div style={s.section}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <div style={s.sectionLabel}>Form Questions</div>
                <div style={{ position: 'relative' }}>
                  <button style={s.btnGhost} onClick={() => setShowTemplateMenu(v => !v)}>+ Add Question</button>
                  {showTemplateMenu && (
                    <div style={{ position: 'absolute', right: 0, top: '36px', background: '#1a1a1a', border: '1px solid var(--border)', borderRadius: '10px', zIndex: 100, minWidth: '300px', maxHeight: '400px', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.6)' }}>
                      <div style={{ padding: '8px 12px', fontSize: '11px', color: '#555', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid var(--border)' }}>Quick Add</div>
                      {QUICK_ADD_TEMPLATES.map((t, i) => {
                        const alreadyAdded = form.fields.some(f => f.field_key === t.field_key);
                        return (
                          <div key={i}
                            onClick={() => !alreadyAdded && addFieldFromTemplate(t)}
                            style={{ padding: '10px 14px', fontSize: '13px', cursor: alreadyAdded ? 'default' : 'pointer', display: 'flex', alignItems: 'center', gap: '10px', color: alreadyAdded ? '#444' : 'var(--text)', borderBottom: '1px solid rgba(255,255,255,0.04)', background: 'transparent' }}
                            onMouseEnter={e => { if (!alreadyAdded) e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                          >
                            <span style={{ fontSize: '15px', flexShrink: 0 }}>{t.icon}</span>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600 }}>{t.label}</div>
                              <div style={{ fontSize: '11px', color: '#555', marginTop: '1px' }}>
                                {t.field_type === 'select' ? `Single choice · ${t.field_options.length} options` : t.field_type === 'multiselect' ? `Multi-select · ${t.field_options.length} options` : t.field_type === 'date' ? 'Date picker' : t.field_type === 'textarea' ? 'Long text' : t.field_type === 'email' ? 'Email' : t.field_type === 'phone' ? 'Phone' : 'Text input'}
                                {alreadyAdded && ' · ✓ Added'}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <div style={{ borderTop: '1px solid var(--border)', padding: '4px' }}>
                        <div
                          onClick={addCustomField}
                          style={{ padding: '10px 14px', fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--yellow)', borderRadius: '6px' }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,215,0,0.08)'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                        >
                          <span style={{ fontSize: '15px' }}>✏️</span>
                          <div>
                            <div style={{ fontWeight: 700 }}>Custom Question</div>
                            <div style={{ fontSize: '11px', color: '#666', marginTop: '1px' }}>Build your own question from scratch</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Field Cards */}
              {form.fields.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#444', fontSize: '13px', border: '1px dashed var(--border)', borderRadius: '8px' }}>
                  Click "+ Add Question" to build your form.
                </div>
              ) : (
                form.fields.map((field, idx) => {
                  const isChoice = field.field_type === 'select' || field.field_type === 'multiselect';
                  const isLink = field.field_type === 'link';
                  const isTextBlock = field.field_type === 'text_block';
                  const isDisplay = DISPLAY_FIELD_TYPES.includes(field.field_type);
                  const linkOpts = Array.isArray(field.field_options) ? field.field_options : [];
                  return (
                    <div key={idx} style={{ ...s.fieldCard, position: 'relative' }}>
                      {/* Question number */}
                      <div style={{ fontSize: '10px', color: '#555', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>
                        Question {idx + 1}
                      </div>

                      {/* Label row */}
                      <div style={{ marginBottom: '10px' }}>
                        <label style={{ ...s.label, marginBottom: '5px', display: 'block' }}>{isDisplay ? `Heading Text${isTextBlock ? ' (optional)' : ''}` : 'Question Label'}</label>
                        <input
                          className="field-input"
                          style={{ fontSize: 14, padding: '8px 12px' }}
                          placeholder="e.g. What would you like to claim?"
                          value={field.field_label}
                          onChange={e => updateField(idx, 'field_label', e.target.value)}
                          autoFocus={field.isNew}
                        />
                      </div>

                      {/* Type + Required row */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '12px', alignItems: 'flex-end', marginBottom: isChoice ? '10px' : '0' }}>
                        <div style={s.field}>
                          <label style={s.label}>Answer Type</label>
                          <select
                            className="field-input"
                            style={{ fontSize: 13, padding: '7px 10px' }}
                            value={field.field_type}
                            onChange={e => {
                              const t = e.target.value;
                              updateField(idx, 'field_type', t);
                              // options mean something different for a link (url/text) vs a choice list
                              // options mean something different per kind (choice list / link url+text / section body)
                              const kind = x => (x === 'select' || x === 'multiselect') ? 'choice' : x === 'link' ? 'link' : x === 'text_block' ? 'text_block' : 'other';
                              if (kind(t) !== kind(field.field_type)) updateField(idx, 'field_options', kind(t) === 'choice' ? ['', ''] : []);
                              if (DISPLAY_FIELD_TYPES.includes(t)) updateField(idx, 'required', false);
                            }}
                          >
                            {FIELD_TYPE_OPTIONS.map(ft => <option key={ft.value} value={ft.value}>{ft.label}</option>)}
                          </select>
                        </div>
                        <div style={{ display: isDisplay ? 'none' : 'flex', alignItems: 'center', gap: '6px', paddingBottom: '8px' }}>
                          <input type="checkbox" id={`req_${idx}`} checked={field.required} onChange={e => updateField(idx, 'required', e.target.checked)} style={{ accentColor: 'var(--yellow)', width: '15px', height: '15px', cursor: 'pointer' }} />
                          <label htmlFor={`req_${idx}`} style={{ ...s.label, cursor: 'pointer' }}>Required</label>
                        </div>
                      </div>

                      {/* Link fields: URL + link text + optional line of text under it */}
                      {isLink && (
                        <div style={{ marginTop: 10 }}>
                          <div style={{ ...s.field, marginBottom: 8 }}>
                            <label style={s.label}>Link URL</label>
                            <input className="field-input" style={{ fontSize: 13, padding: '7px 10px' }} placeholder="https://..."
                              value={linkOpts[0] || ''}
                              onChange={e => updateField(idx, 'field_options', [e.target.value, linkOpts[1] || ''])} />
                            {linkOpts[0] && !/^https?:\/\//i.test(linkOpts[0].trim()) && (
                              <div style={{ color: 'var(--red, #e5484d)', fontSize: 12, marginTop: 4 }}>Must start with https:// — the link won't show on the form otherwise.</div>
                            )}
                          </div>
                          <div style={{ ...s.field, marginBottom: 8 }}>
                            <label style={s.label}>Link Text <span style={{ color: '#555', fontWeight: 400 }}>(optional — defaults to the URL)</span></label>
                            <input className="field-input" style={{ fontSize: 13, padding: '7px 10px' }} placeholder="e.g. Donate here"
                              value={linkOpts[1] || ''}
                              onChange={e => updateField(idx, 'field_options', [linkOpts[0] || '', e.target.value])} />
                          </div>
                          <div style={s.field}>
                            <label style={s.label}>Text Below Link <span style={{ color: '#555', fontWeight: 400 }}>(optional — blue italic)</span></label>
                            <input className="field-input" style={{ fontSize: 12, padding: '6px 10px' }} placeholder="e.g. All proceeds go to Heart of the City"
                              value={field.disclaimer || ''} onChange={e => updateField(idx, 'disclaimer', e.target.value)} />
                          </div>
                        </div>
                      )}

                      {isTextBlock && (
                        <TextSectionEditor
                          labelStyle={s.label}
                          value={(Array.isArray(field.field_options) && field.field_options[0]) || ''}
                          onChange={body => updateField(idx, 'field_options', [body])} />
                      )}

                      {/* Options builder for choice fields */}
                      {isChoice && (
                        <div>
                          <label style={{ ...s.label, display: 'block', marginBottom: '6px' }}>
                            Answer Options
                            <span style={{ color: '#555', fontWeight: 400, marginLeft: '6px' }}>
                              {field.field_type === 'multiselect' ? '(people can select all that apply)' : '(people pick one)'}
                            </span>
                          </label>
                          {(field.field_options || []).map((opt, oi) => {
                            // An option is a plain string, or {label, write_in, placeholder}
                            // when picking it should open a text box on the form
                            // (e.g. "I have a partner: ___", "Other: please specify").
                            const optLabel = typeof opt === 'string' ? opt : (opt && opt.label) || '';
                            const writeIn = opt && typeof opt === 'object' ? opt.write_in || '' : '';
                            const setOpt = next => {
                              const opts = [...(field.field_options || [])];
                              opts[oi] = next.write_in ? next : next.label;
                              updateField(idx, 'field_options', opts);
                            };
                            const cur = { label: optLabel, write_in: writeIn, placeholder: (opt && opt.placeholder) || '' };
                            return (
                            <div key={oi} style={{ marginBottom: '8px' }}>
                            <div style={{ display: 'flex', gap: '6px' }}>
                              <input
                                className="field-input"
                                style={{ fontSize: 13, padding: '6px 10px', flex: 1 }}
                                value={optLabel}
                                placeholder={`Option ${oi + 1}`}
                                onChange={e => setOpt({ ...cur, label: e.target.value })}
                              />
                              <select className="field-input" title="Add a text box when this option is picked"
                                style={{ fontSize: 12, padding: '6px 8px', width: 'auto', flex: '0 0 auto' }}
                                value={writeIn}
                                onChange={e => setOpt({ ...cur, write_in: e.target.value })}>
                                <option value="">No follow-up</option>
                                <option value="optional">If picked, ask for text (optional)</option>
                                <option value="required">If picked, ask for text (required)</option>
                              </select>
                              <button style={{ ...s.btnSmall, padding: '4px 8px' }} disabled={oi === 0}
                                onClick={() => { const opts = [...(field.field_options||[])]; [opts[oi-1],opts[oi]]=[opts[oi],opts[oi-1]]; updateField(idx,'field_options',opts); }}>↑</button>
                              <button style={{ ...s.btnSmall, padding: '4px 8px' }} disabled={oi === (field.field_options||[]).length-1}
                                onClick={() => { const opts = [...(field.field_options||[])]; [opts[oi],opts[oi+1]]=[opts[oi+1],opts[oi]]; updateField(idx,'field_options',opts); }}>↓</button>
                              <button style={s.btnSmallDanger} onClick={() => {
                                const opts = (field.field_options || []).filter((_, i) => i !== oi);
                                updateField(idx, 'field_options', opts);
                              }}>✕</button>
                            </div>
                            {writeIn && (
                              <input className="field-input" style={{ fontSize: 12, padding: '5px 10px', marginTop: 4, marginLeft: 16, width: 'calc(100% - 16px)' }}
                                placeholder="What should they type? (e.g. Partner's full name)"
                                value={cur.placeholder}
                                onChange={e => setOpt({ ...cur, placeholder: e.target.value })} />
                            )}
                            </div>
                            );
                          })}
                          <button
                            style={{ ...s.btnSmall, marginTop: '2px', color: 'var(--yellow)', borderColor: 'rgba(255,215,0,0.3)' }}
                            onClick={() => updateField(idx, 'field_options', [...(field.field_options || []), ''])}
                          >+ Add Option</button>
                          <ChoicePreview field={field} />
                          <div style={{ marginTop: 10 }}>
                            <label style={{ ...s.label, fontSize: 11 }}>Question Disclaimer <span style={{ color: '#555', fontWeight: 400 }}>(optional — shown below options in blue italic)</span></label>
                            <input className="field-input" style={{ fontSize: 12, padding: '6px 10px' }} placeholder="*Eligible for Free Pickleball Redemption" value={field.disclaimer || ''} onChange={e => updateField(idx, 'disclaimer', e.target.value)} />
                          </div>
                        </div>
                      )}

                      {/* Card actions */}
                      <div style={{ display: 'flex', gap: '6px', marginTop: '12px', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.05)', justifyContent: 'flex-end' }}>
                        <button style={s.btnSmall} onClick={() => moveField(idx, -1)} disabled={idx === 0}>↑ Move up</button>
                        <button style={s.btnSmall} onClick={() => moveField(idx, 1)} disabled={idx === form.fields.length - 1}>↓ Move down</button>
                        <button style={s.btnSmallDanger} onClick={() => removeField(idx)}>Remove</button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Submit — after the questions, same order as the form itself */}
            <div style={s.section}>
              <div style={s.sectionLabel}>Submit</div>
              <div style={s.field}>
                <label style={s.label}>Form Disclaimer <span style={{ color: '#555', fontWeight: 400 }}>(optional — blue italic fine print)</span></label>
                <textarea className="field-input" style={{ fontSize: 13, padding: '8px 12px', minHeight: '60px', resize: 'vertical' }} placeholder="*Eligible for Free Pickleball Redemption&#10;*Memberships available for as low as $49/mo." value={form.form_disclaimer || ''} onChange={e => setForm(f => ({ ...f, form_disclaimer: e.target.value }))} />
                {(form.form_disclaimer || '').trim() && (
                  <div style={{ display: 'flex', gap: 16, marginTop: 6, fontSize: 12, color: '#aaa' }}>
                    {[['above', 'Above the Submit button'], ['below', 'Below the Submit button']].map(([v, l]) => (
                      <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                        <input type="radio" name="disclaimer_position" style={{ accentColor: 'var(--yellow)' }}
                          checked={(form.form_disclaimer_position || 'above') === v}
                          onChange={() => setForm(f => ({ ...f, form_disclaimer_position: v }))} />
                        {l}
                      </label>
                    ))}
                  </div>
                )}
                {(form.form_disclaimer || '').trim() && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, color: '#aaa' }}>Style:</span>
                    {Object.entries(DISCLAIMER_STYLE_OPTIONS).map(([v, o]) => {
                      const on = (form.form_disclaimer_style || 'blue_italic') === v;
                      return (
                        <button key={v} type="button"
                          onClick={() => setForm(f => ({ ...f, form_disclaimer_style: v }))}
                          style={{ background: on ? 'var(--fill-primary)' : 'transparent', color: on ? 'var(--on-primary)' : '#aaa', border: '1px solid var(--border)', borderRadius: 8, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                          {o.label}
                        </button>
                      );
                    })}
                  </div>
                )}
                {(form.form_disclaimer || '').trim() && (
                  <div style={{ marginTop: 8, background: '#fff', borderRadius: 8, padding: '10px 12px', textAlign: 'center', fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-line', ...DISCLAIMER_STYLE_OPTIONS[form.form_disclaimer_style || 'blue_italic'].css }}>
                    {form.form_disclaimer}
                  </div>
                )}
                {/* Consent wording ("By submitting...") should stay above the button */}
              </div>
              <div style={s.field}>
                <label style={s.label}>Text-Message Consent <span style={{ color: '#555', fontWeight: 400 }}>(the "I agree to receive recurring automated text messages" checkbox on every form)</span></label>
                <div style={{ display: 'flex', gap: 16, fontSize: 13, color: '#aaa', flexWrap: 'wrap' }}>
                  {[[true, 'Required to submit'], [false, 'Optional (recommended for waiver forms)']].map(([v, l]) => (
                    <label key={String(v)} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                      <input type="radio" name="sms_consent_required" style={{ accentColor: 'var(--yellow)' }}
                        checked={(form.sms_consent_required !== false) === v}
                        onChange={() => setForm(f => ({ ...f, sms_consent_required: v }))} />
                      {l}
                    </label>
                  ))}
                </div>
                {form.sms_consent_required === false && (
                  <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>Everyone is still captured as a lead. Guests who leave it unchecked are flagged "No auto-texts" in Inbound Leads: personal follow-up is fine, just keep them out of automated texts.</div>
                )}
              </div>
              <div style={s.field}>
                <label style={s.label}>Submit Button Text <span style={{ color: '#555', fontWeight: 400 }}>(optional — blank shows SUBMIT; e.g. "Submit &amp; Go to Donation Page" with the redirect below)</span></label>
                <input className="field-input" style={{ fontSize: 14, padding: '8px 12px' }} placeholder="SUBMIT" maxLength={60} value={form.submit_label || ''} onChange={e => setForm(f => ({ ...f, submit_label: e.target.value }))} />
              </div>
              <div style={s.field}>
                <label style={s.label}>Post-Submit Redirect URL <span style={{ color: '#555', fontWeight: 400 }}>(optional — blank shows default success screen)</span></label>
                <input className="field-input" style={{ fontSize: 14, padding: '8px 12px' }} placeholder="https://thepicklr.com/location/west-allis-wi/" value={form.redirect_url || ''} onChange={e => setForm(f => ({ ...f, redirect_url: e.target.value }))} />
              </div>
            </div>

            {/* URL Preview */}
            {(editingActivation || formUrlPreview) && (
              <div style={s.section}>
                <div style={s.sectionLabel}>Form URL</div>
                <div style={s.urlBox}>
                  <span>playpicklr.com/{editingActivation ? editingActivation.activation_id : `${formUrlPreview}-...`}</span>
                  {editingActivation && (
                    <button style={s.copyBtn} onClick={() => copyUrl(editingActivation.activation_id)}>
                      {copiedId === editingActivation.activation_id ? '✓ Copied' : 'Copy'}
                    </button>
                  )}
                </div>
              </div>
            )}

            <div style={s.modalFooter}>
              <button style={s.btnGhost} onClick={() => setShowModal(false)}>Cancel</button>
              {editingActivation && (
                <button style={{ ...s.btnGhost, color: 'var(--yellow)', borderColor: 'rgba(255,215,0,0.3)' }}
                  onClick={async () => {
                    const newStatus = editingActivation.status === 'active' ? 'planning' : 'active';
                    await put(`/activations/${editingActivation.activation_id}`, { status: newStatus });
                    setShowModal(false); load();
                  }}>
                  {editingActivation.status === 'active' ? 'Deactivate' : 'Set Active'}
                </button>
              )}
              <button
                style={{ ...s.btnYellow, opacity: (!form.name || !form.type || !form.primary_location_id || saving) ? 0.5 : 1 }}
                onClick={save}
                disabled={!form.name || !form.type || !form.primary_location_id || saving}
              >
                {saving ? 'Saving...' : editingActivation ? 'Save Changes' : 'Create Activation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QR + Print Modal */}
      {qrActivation && (
        <QRModal activation={qrActivation} onClose={() => setQrActivation(null)} />
      )}
    </div>
  );
}

// ── Partner logo upload ───────────────────────────────────────────────────────
// Uploads straight to COMS (POST /api/activation-logos, stored in Postgres) and
// writes the returned public URL into partner_logo_N_url — replaces the old
// "upload to murkostorage by hand, paste the URL" step. Pasting a URL still
// works for logos already hosted elsewhere.
const LOGO_MAX_BYTES = 1024 * 1024;
const LOGO_MAX_DIM = 1200;

// Crops the empty border off a logo before upload (white or transparent
// padding, e.g. a wide mark centered on a square canvas) so it sizes against
// the Picklr mark by its real artwork, not its padding. Also caps dimensions.
// Returns the original file untouched if there's nothing worth trimming.
async function trimLogo(file) {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = url;
    });
    const w = img.naturalWidth, h = img.naturalHeight;
    const c = document.createElement('canvas');
    c.width = w; c.height = h;
    const ctx = c.getContext('2d');
    ctx.drawImage(img, 0, 0);
    const px = ctx.getImageData(0, 0, w, h).data;
    let top = h, left = w, right = -1, bottom = -1;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const o = (y * w + x) * 4;
        const bg = px[o + 3] < 16 || (px[o] > 240 && px[o + 1] > 240 && px[o + 2] > 240);
        if (bg) continue;
        if (x < left) left = x; if (x > right) right = x;
        if (y < top) top = y; if (y > bottom) bottom = y;
      }
    }
    if (right < 0) return file; // blank image — let the server reject/keep as-is
    const pad = Math.round(Math.max(right - left, bottom - top) * 0.03);
    left = Math.max(0, left - pad); top = Math.max(0, top - pad);
    right = Math.min(w - 1, right + pad); bottom = Math.min(h - 1, bottom + pad);
    const cw = right - left + 1, ch = bottom - top + 1;
    const scale = Math.min(1, LOGO_MAX_DIM / Math.max(cw, ch));
    if (cw * ch > w * h * 0.9 && scale === 1) return file; // already tight
    const out = document.createElement('canvas');
    out.width = Math.round(cw * scale); out.height = Math.round(ch * scale);
    out.getContext('2d').drawImage(c, left, top, cw, ch, 0, 0, out.width, out.height);
    const blob = await new Promise(resolve => out.toBlob(resolve, 'image/png'));
    return blob ? new File([blob], file.name.replace(/\.\w+$/, '') + '.png', { type: 'image/png' }) : file;
  } catch (e) {
    return file; // trimming is a nicety; never block an upload on it
  } finally {
    URL.revokeObjectURL(url);
  }
}

function PartnerLogoField({ label, hint, value, onChange, labelStyle, fieldStyle }) {
  const { getAccessTokenSilently } = useAuth0();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [showUrl, setShowUrl] = useState(false);

  async function handleFile(file) {
    setError('');
    if (!file) return;
    if (!['image/png', 'image/jpeg'].includes(file.type)) { setError('Use a PNG or JPEG (PNG with a transparent background looks best).'); return; }
    setUploading(true);
    file = await trimLogo(file);
    if (file.size > LOGO_MAX_BYTES) { setUploading(false); setError('Logo must be 1 MB or smaller.'); return; }
    const API_BASE = process.env.REACT_APP_API_BASE || 'https://murko-coms-api.azurewebsites.net';
    try {
      const token = await getAccessTokenSilently();
      const res = await fetch(`${API_BASE}/api/activation-logos?filename=${encodeURIComponent(file.name)}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': file.type },
        body: file,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      onChange(data.url);
    } catch (e) {
      setError(e.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  const btn = { background: 'transparent', color: '#aaa', border: '1px solid var(--border)', borderRadius: '8px', padding: '7px 14px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' };

  return (
    <div style={fieldStyle}>
      <label style={labelStyle}>{label} <span style={{ color: '#555', fontWeight: 400 }}>({hint})</span></label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        {value ? (
          <div style={{ background: '#fff', borderRadius: 8, padding: 8, height: 64, minWidth: 64, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src={value} alt={label} style={{ maxHeight: 48, maxWidth: 160, objectFit: 'contain' }}
              onError={() => setError("Couldn't load this image — check the URL or upload the file instead.")} />
          </div>
        ) : null}
        <input ref={fileRef} type="file" accept="image/png,image/jpeg" style={{ display: 'none' }}
          onChange={e => handleFile(e.target.files && e.target.files[0])} />
        <button type="button" style={btn} disabled={uploading} onClick={() => fileRef.current && fileRef.current.click()}>
          {uploading ? 'Uploading…' : value ? 'Replace' : '⬆️ Upload logo'}
        </button>
        {value ? <button type="button" style={btn} onClick={() => { setError(''); onChange(''); }}>Remove</button> : null}
        <button type="button" style={{ ...btn, border: 'none', padding: '7px 4px', fontWeight: 400 }} onClick={() => setShowUrl(v => !v)}>
          {showUrl ? 'Hide URL' : 'or paste a URL'}
        </button>
      </div>
      {showUrl ? (
        <input className="field-input" style={{ fontSize: 14, padding: '8px 12px', marginTop: 8 }} placeholder="https://..."
          value={value} onChange={e => { setError(''); onChange(e.target.value); }} />
      ) : null}
      {error ? <div style={{ color: 'var(--red, #e5484d)', fontSize: 12, marginTop: 6 }}>{error}</div> : null}
    </div>
  );
}

// ── QR + Print Modal ──────────────────────────────────────────────────────────
function QRModal({ activation, onClose }) {
  const { getAccessTokenSilently } = useAuth0();
  const qrRef = useRef(null);
  const [qrLoaded, setQrLoaded] = useState(false);
  const [mode, setMode] = useState('rsvp'); // 'rsvp' | 'checkin'
  const formUrl = `https://www.playpicklr.com/${activation.activation_id}${mode === 'checkin' ? '?checkin=1' : ''}`;
  const displayUrl = `playpicklr.com/${activation.activation_id}${mode === 'checkin' ? '?checkin=1' : ''}`;

  const CLUB_NAMES = { WA: 'West Allis', MF: 'Menomonee Falls', GB: 'Green Bay', DEN: 'Denton', LEW: 'Lewisville', MCK: 'McKinney' };

  useEffect(() => {
    function renderQR() {
      if (!qrRef.current) return;
      qrRef.current.innerHTML = ''; // clear so switching modes regenerates instead of no-op
      new window.QRCode(qrRef.current, {
        text: formUrl,
        width: 220,
        height: 220,
        colorDark: '#000000',
        colorLight: '#ffffff',
        correctLevel: window.QRCode.CorrectLevel.H,
      });
      setQrLoaded(true);
    }
    if (window.QRCode) { renderQR(); return; }
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js';
    script.onload = renderQR;
    document.head.appendChild(script);
  }, [formUrl]);

  async function handlePrint() {
    const clubName = CLUB_NAMES[activation.primary_location_id] || activation.primary_location_id;
    const qrCanvas = qrRef.current && qrRef.current.querySelector('canvas');
    const qrDataUrl = qrCanvas ? qrCanvas.toDataURL('image/png') : '';
    // Sprint 19.9: activations can now carry multiple offers (activation_offers
    // table) instead of one. Fall back to the legacy single `offer` field for
    // any activation that predates the migration and somehow has no rows yet.
    const offerList = (activation.offers && activation.offers.length > 0)
      ? activation.offers
      : (activation.offer ? [{ offer_label: activation.offer_label, offer_text: activation.offer }] : []);
    const offerBlocksHtml = offerList.map(o => {
      const lines = (o.offer_text || '').split('\n').filter(Boolean);
      const title = lines[0] || '';
      const desc = lines.slice(1).join(' ') || '';
      if (!title) return '';
      return `<div class="offer-block"><div class="event-special">${o.offer_label || 'Event Special'}</div><div class="offer-title">${title}</div>${desc ? `<div class="offer-desc">${desc}</div>` : ''}</div>`;
    }).join('');

    // Fetch logo as base64 to avoid cross-origin issues in print window
    let logoDataUrl = '';
    try {
      const logoRes = await fetch('https://murkostorage.blob.core.windows.net/branding/PWordmark.png');
      if (logoRes.ok) {
        const blob = await logoRes.blob();
        logoDataUrl = await new Promise(resolve => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      }
    } catch (e) { /* logo unavailable */ }

    const picklrMarkHtml = logoDataUrl
      ? `<img class="logo" src="${logoDataUrl}" />`
      : `<div style="font-size:28px;font-weight:900;letter-spacing:-1px;">PICKLR</div>`;

    // Partner logos (co-branding) — inlined as data URLs for the same reason
    // as the wordmark; one that fails to load is just left off the sheet.
    const partnerDataUrls = (await Promise.all(
      [activation.partner_logo_1_url, activation.partner_logo_2_url].filter(Boolean).map(async url => {
        try {
          const r = await fetch(url);
          if (!r.ok) return null;
          const b = await r.blob();
          return await new Promise(resolve => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(b);
          });
        } catch (e) { return null; }
      })
    )).filter(Boolean);
    const logoHtml = `<div class="logo-row">${picklrMarkHtml}${partnerDataUrls.map(u => `<img class="logo partner" src="${u}" />`).join('')}</div>`;

    const scanCta = mode === 'checkin' ? 'Scan to check in →' : 'Scan to sign up →';
    const win = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html>
<html><head><meta charset="UTF-8"/>
<title>${activation.name}</title>
<style>
  @page { size: 8.5in 11in; margin: 0; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, sans-serif; width: 8.5in; height: 11in; display: flex; align-items: center; justify-content: center; background: #fff; -webkit-print-color-adjust: exact; }
  .tent { width: 6.5in; text-align: center; }
  .logo-row { display: flex; align-items: center; justify-content: center; gap: 36px; margin-bottom: 20px; }
  .logo { height: 60px; width: auto; }
  .logo.partner { height: auto; max-height: 70px; max-width: 2.2in; object-fit: contain; }
  .thank { font-size: 14px; color: #333; margin-bottom: 8px; }
  .event-name { font-size: 42px; font-weight: 900; color: #000; margin-bottom: 14px; line-height: 1.1; }
  .tagline { font-size: 15px; font-weight: 600; color: #333; margin-bottom: 28px; }
  .offer-block { border: 2px solid #e0e0e0; border-radius: 10px; padding: 18px 28px; margin-bottom: 32px; background: #fafafa; }
  .event-special { font-size: 13px; font-weight: 800; color: #e85d04; text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 6px; }
  .offer-title { font-size: 22px; font-weight: 800; color: #0075E7; margin-bottom: 8px; }
  .offer-desc { font-size: 14px; font-style: italic; color: #555; line-height: 1.5; }
  .qr-section { margin-bottom: 20px; }
  .qr-img { width: 220px; height: 220px; display: block; margin: 0 auto 12px; }
  .scan-cta { font-size: 16px; font-weight: 800; color: #000; margin-bottom: 6px; }
  .form-url { font-size: 13px; color: #555; font-family: monospace; margin-bottom: 20px; }
  .club-footer { font-size: 12px; color: #999; border-top: 1px solid #eee; padding-top: 16px; margin-top: 8px; }
</style>
</head><body><div class="tent">
  ${logoHtml}
  <div class="thank">Thank you for visiting us at the</div>
  <div class="event-name">${activation.name}</div>
  ${activation.tagline_hidden ? '' : `<div class="tagline">${String(activation.tagline || 'Please fill out the below information to receive updates about our club.').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))}</div>`}
  ${mode === 'checkin' ? '' : offerBlocksHtml}
  <div class="qr-section">
    ${qrDataUrl ? `<img class="qr-img" src="${qrDataUrl}" />` : '<div style="width:220px;height:220px;background:#f5f5f5;margin:0 auto 12px;display:flex;align-items:center;justify-content:center;font-size:12px;color:#aaa;border:1px solid #e0e0e0;">QR Code</div>'}
    <div class="scan-cta">${scanCta}</div>
    <div class="form-url">${displayUrl}</div>
  </div>
  <div class="club-footer">${clubName}</div>
</div></body></html>`);
    win.document.close();
    setTimeout(() => win.print(), 800);
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.82)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: '#111', border: '1px solid var(--border)', borderRadius: '16px', width: '100%', maxWidth: '420px', padding: '32px', textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text)' }}>QR Code + Print</div>
          <button style={{ background: 'none', border: 'none', color: '#666', fontSize: '20px', cursor: 'pointer' }} onClick={onClose}>✕</button>
        </div>
        <div style={{ fontSize: '13px', color: '#888', marginBottom: '20px' }}>{activation.name}</div>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginBottom: '20px' }}>
          <button
            style={{
              background: mode === 'rsvp' ? 'var(--fill-primary)' : 'transparent',
              color: mode === 'rsvp' ? 'var(--on-primary)' : '#aaa',
              border: '1px solid var(--border)', borderRadius: '8px', padding: '7px 14px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
            }}
            onClick={() => setMode('rsvp')}>
            RSVP Form
          </button>
          <button
            style={{
              background: mode === 'checkin' ? 'var(--fill-primary)' : 'transparent',
              color: mode === 'checkin' ? 'var(--on-primary)' : '#aaa',
              border: '1px solid var(--border)', borderRadius: '8px', padding: '7px 14px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
            }}
            onClick={() => setMode('checkin')}>
            Day-of Check-in
          </button>
        </div>
        <div style={{ background: '#fff', borderRadius: '12px', padding: '16px', display: 'inline-block', marginBottom: '16px' }}>
          <div ref={qrRef} />
          {!qrLoaded && (
            <div style={{ width: 220, height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa', fontSize: '13px' }}>
              Generating...
            </div>
          )}
        </div>
        <div style={{ fontFamily: 'monospace', fontSize: '12px', color: '#666', marginBottom: '24px', wordBreak: 'break-all' }}>
          {displayUrl}
        </div>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            style={{ background: 'transparent', color: '#aaa', border: '1px solid var(--border)', borderRadius: '8px', padding: '9px 18px', fontSize: '13px', cursor: 'pointer' }}
            onClick={() => navigator.clipboard.writeText(formUrl).catch(() => {})}>
            🔗 Copy URL
          </button>
          <button
            style={{ background: 'transparent', color: '#aaa', border: '1px solid var(--border)', borderRadius: '8px', padding: '9px 18px', fontSize: '13px', cursor: 'pointer' }}
            onClick={() => {
              const canvas = qrRef.current && qrRef.current.querySelector('canvas');
              if (!canvas) return;
              const link = document.createElement('a');
              link.download = `${activation.activation_id}-${mode}-qr.png`;
              link.href = canvas.toDataURL('image/png');
              link.click();
            }}>
            ⬇️ Download QR
          </button>
          <button
            style={{ background: 'transparent', color: '#aaa', border: '1px solid var(--border)', borderRadius: '8px', padding: '9px 18px', fontSize: '13px', cursor: 'pointer' }}
            onClick={async () => {
              const API_BASE = process.env.REACT_APP_API_BASE || 'https://murko-coms-api.azurewebsites.net';
              try {
                const token = await getAccessTokenSilently();
                const res = await fetch(`${API_BASE}/api/activations/${activation.activation_id}/export-docx`, {
                  headers: { Authorization: `Bearer ${token}` },
                });
                if (!res.ok) throw new Error('Export failed');
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${activation.activation_id}-table-tent.docx`;
                link.click();
                URL.revokeObjectURL(url);
              } catch (e) {
                alert('Word doc export failed. Please try again.');
              }
            }}>
            📄 Word Doc
          </button>
          <button
            style={{ background: 'var(--yellow)', color: '#000', border: 'none', borderRadius: '8px', padding: '9px 18px', fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}
            onClick={handlePrint}>
            🖨️ Print 8.5×11
          </button>
        </div>
      </div>
    </div>
  );
}

export default ActivationsModule;
